import { type ReactNode, useState } from "react"
import { IconDeviceFloppy, IconLoader2 } from "@tabler/icons-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Separator } from "@/components/ui/separator"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import { type BookingSeasonSnapshot, useSaveBookingSeasonMutation } from "../bookingSeasonApi"
import { closedReasonLabel, formatSeasonDateTime } from "../bookingSeasonFormat"
import {
  buildSaveRequest,
  type FieldIssue,
  isSaveRefusal,
  MESSAGE_MAX_LENGTH,
  pacificInputToIso,
  type SeasonField,
  type SeasonFormState,
  statusAfterSave,
  validateSeasonForm,
} from "../lib/seasonForm"
import { PhoneMessagePreview, WebsiteMessagePreview } from "./MessagePreview"

interface SeasonFormProps {
  form: SeasonFormState
  onChange: (form: SeasonFormState) => void
  /** True when the form still matches the current season. */
  unchanged: boolean
  onReset: () => void
  /** Called with the saved season, so the page can follow it. */
  onSaved: (season: BookingSeasonSnapshot) => void
}

/** The booking season editor: the two Pacific date-times and the three closed messages. */
export function SeasonForm({ form, onChange, unchanged, onReset, onSaved }: SeasonFormProps) {
  // Field issues stay hidden until the first save attempt, so a fresh form isn't covered in red.
  const [attempted, setAttempted] = useState(false)
  const [serverErrors, setServerErrors] = useState<string[]>([])
  const [save, { isLoading: saving }] = useSaveBookingSeasonMutation()

  const issues = validateSeasonForm(form)
  const shownIssues = attempted ? issues : []
  const request = buildSaveRequest(form)

  const update = (patch: Partial<SeasonFormState>) => {
    setServerErrors([])
    onChange({ ...form, ...patch })
  }

  async function handleSave() {
    setAttempted(true)
    setServerErrors([])
    if (!request) return

    try {
      const saved = await save(request).unwrap()
      setAttempted(false)
      onSaved(saved)
      toast.success("Booking season saved.", {
        description:
          saved.status === "Open" && saved.closesAt
            ? `Public booking is open until ${formatSeasonDateTime(saved.closesAt)}.`
            : `Public booking is closed (${closedReasonLabel(saved) ?? "Closed"}).`,
      })
    } catch (err) {
      const error = err as { status?: number; data?: unknown }

      if (error.status === 400 && isSaveRefusal(error.data)) {
        setServerErrors(error.data.errors)
      } else if (error.status === 403) {
        setServerErrors(["You do not have permission to change the booking season."])
      } else {
        setServerErrors(["Could not save the booking season. Try again in a moment."])
      }
    }
  }

  function handleDiscard() {
    setAttempted(false)
    setServerErrors([])
    onReset()
  }

  const willBe = request ? statusAfterSave(request, new Date()) : null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Season</CardTitle>
        <CardDescription>
          Public booking opens at the first time and stops at the second. Saving replaces the current season
          straight away.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <DateTimeField
            id="season-opens"
            label="Booking opens (Pacific time)"
            hint="Public booking starts at this moment."
            value={form.opensAt}
            issues={issuesFor(shownIssues, "opensAt")}
            onChange={(opensAt) => update({ opensAt })}
          />
          <DateTimeField
            id="season-closes"
            label="Booking closes (Pacific time)"
            hint="Public booking stops at this moment."
            value={form.closesAt}
            issues={issuesFor(shownIssues, "closesAt")}
            onChange={(closesAt) => update({ closesAt })}
          />
        </div>

        <Separator />

        <div className="flex flex-col gap-1">
          <h3 className="font-medium">Closed messages</h3>
          <p className="text-sm text-muted-foreground">
            What the public is told whenever booking is closed — before the season, after it, or after "Close
            booking now". Staff booking is never blocked.
          </p>
        </div>

        <MessageField
          id="season-web-message"
          label="Website message"
          hint="Shown on the public booking page."
          value={form.closedWebMessage}
          issues={issuesFor(shownIssues, "closedWebMessage")}
          onChange={(closedWebMessage) => update({ closedWebMessage })}
          preview={<WebsiteMessagePreview message={form.closedWebMessage} />}
        />

        <MessageField
          id="season-phone-english"
          label="Phone message (English)"
          hint={'Read aloud to callers by text-to-speech. Spell web addresses out as they should be spoken, e.g. "P S E dot com" rather than "pse.com".'}
          value={form.closedPhoneMessageEnglish}
          issues={issuesFor(shownIssues, "closedPhoneMessageEnglish")}
          onChange={(closedPhoneMessageEnglish) => update({ closedPhoneMessageEnglish })}
          preview={<PhoneMessagePreview message={form.closedPhoneMessageEnglish} />}
        />

        <MessageField
          id="season-phone-spanish"
          label="Phone message (Spanish)"
          optional
          hint={'Read aloud to callers who choose Spanish. Leave it blank and they hear the English message. Spell web addresses out, e.g. "P S E punto com".'}
          value={form.closedPhoneMessageSpanish}
          issues={issuesFor(shownIssues, "closedPhoneMessageSpanish")}
          onChange={(closedPhoneMessageSpanish) => update({ closedPhoneMessageSpanish })}
          preview={
            <PhoneMessagePreview message={form.closedPhoneMessageSpanish} fallback={form.closedPhoneMessageEnglish} />
          }
        />

        {serverErrors.length > 0 && (
          <div className="flex flex-col gap-1 rounded-lg border border-destructive/50 p-3 text-sm text-destructive" role="alert">
            <span className="font-medium">The season was not saved:</span>
            <ul className="list-disc pl-5">
              {serverErrors.map((error) => (
                <li key={error}>{error}</li>
              ))}
            </ul>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {unchanged
            ? "No changes to save."
            : willBe === "Open"
              ? "Once saved, public booking will be open."
              : willBe === "Closed"
                ? "Once saved, public booking will be closed."
                : attempted && issues.length > 0
                  ? "Fix the problems above to save."
                  : null}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleDiscard} disabled={unchanged || saving}>
            Discard changes
          </Button>
          <Button onClick={() => void handleSave()} disabled={unchanged || saving}>
            {saving ? <IconLoader2 className="size-4 animate-spin" /> : <IconDeviceFloppy className="size-4" />}
            Save season
          </Button>
        </div>
      </CardFooter>
    </Card>
  )
}

function issuesFor(issues: FieldIssue[], field: SeasonField): string[] {
  return issues.filter((issue) => issue.field === field).map((issue) => issue.message)
}

function FieldIssues({ id, issues }: { id: string; issues: string[] }) {
  if (issues.length === 0) return null
  return (
    <div id={id} role="alert" className="flex flex-col gap-0.5 text-sm text-destructive">
      {issues.map((issue) => (
        <span key={issue}>{issue}</span>
      ))}
    </div>
  )
}

interface DateTimeFieldProps {
  id: string
  label: string
  hint: string
  value: string
  issues: string[]
  onChange: (value: string) => void
}

/** A `datetime-local` input read as Pacific wall time, echoing back the instant it means. */
function DateTimeField({ id, label, hint, value, issues, onChange }: DateTimeFieldProps) {
  const parsed = pacificInputToIso(value)

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="datetime-local"
        value={value}
        aria-invalid={issues.length > 0}
        aria-describedby={`${id}-hint ${id}-issues`}
        onChange={(e) => onChange(e.target.value)}
      />
      <p id={`${id}-hint`} className="text-xs text-muted-foreground">
        {parsed.ok ? `${formatSeasonDateTime(parsed.iso)}. ${hint}` : hint}
      </p>
      <FieldIssues id={`${id}-issues`} issues={issues} />
    </div>
  )
}

interface MessageFieldProps {
  id: string
  label: string
  hint: string
  optional?: boolean
  value: string
  issues: string[]
  onChange: (value: string) => void
  preview: ReactNode
}

function MessageField({ id, label, hint, optional, value, issues, onChange, preview }: MessageFieldProps) {
  const length = value.trim().length

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="flex flex-col gap-2">
        <Label htmlFor={id}>
          {label}
          {optional && <span className="text-muted-foreground font-normal"> (optional)</span>}
        </Label>
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
        <Textarea
          id={id}
          value={value}
          rows={5}
          aria-invalid={issues.length > 0}
          aria-describedby={`${id}-hint ${id}-issues`}
          onChange={(e) => onChange(e.target.value)}
        />
        <div className="flex items-start justify-between gap-2">
          <FieldIssues id={`${id}-issues`} issues={issues} />
          <span
            className={cn("ml-auto text-xs tabular-nums", length > MESSAGE_MAX_LENGTH ? "text-destructive" : "text-muted-foreground")}
          >
            {length} / {MESSAGE_MAX_LENGTH}
          </span>
        </div>
      </div>
      {preview}
    </div>
  )
}
